import Moment from 'moment';
import { formatEpochToDateTime } from 'utils/formatter.js';

export class Validate {

  /* Called for individual updates */
  static parseOrderSummary(order, isOTR) {
    const { expenses, broker, brokerOrderNumber, fuelCharges, fuelChargesType, notes } = order;
    const summary = {
      broker: broker,
      brokerOrderNumber: brokerOrderNumber,
      fuelCharges: fuelCharges,
      fuelChargesType: fuelChargesType,
      notes: notes,
      expenses: expenses,
    };
    if(isOTR) {
      summary.loadType = order.loadType;
      summary.lineHaulCharges = order.lineHaulCharges;
      summary.weight = order.weight;
      summary.length = order.length;
    } else {
      summary.orderType = order.orderType;
      summary.orderRate = order.orderRate;
      summary.appointment = order.appointment;
    }
    return summary;
  }

  static parseOrderOrigin(input, isOTR) {
    const origin = {
      id: input.id,
      name: input,
      appointment: formatEpochToDateTime(input.appointment),
      isOpenTime: input.openTime,
      openAppointment: input.openAppointment,
      puNumber: input.puNumber,
      puLocation: input.puLocation,
      notes: input.notes,
    };
    return origin;
  }

  static parseOrderDestination(input, isOTR) {
    const destination = {
      id: input.id,
      name: input,
      appointment: formatEpochToDateTime(input.appointment),
      isOpenTime: input.openTime,
      openAppointment: input.openAppointment,
      referenceNumber: input.referenceNumber,
      notes: input.notes,
    };
    return destination;
  }

  /* Called when we edit order form */
  static parseDispatchOrder(order) {
    const { id, broker, container, chassis, expenses, legs } = order;
    /* Parse Order to construct OrderForm */
    const dispatchOrder = {};
    dispatchOrder.id = id;
    dispatchOrder.summary = {
      broker: broker,
      brokerOrderNumber: order.brokerOrderNumber,
      orderType: order.orderType,
      orderRate: order.orderRate,
      fuelCharges: order.fuelCharges,
      fuelChargesType: order.fuelChargesType,
      appointment: order.appointment,
      palletQty: order.palletQty,
      temperature: order.temperature,
      tempUnit: order.tempUnit,
      notes: order.notes,
      expenses: expenses,
    };
    if (legs) {
      const { origins, destinations } = legs[legs.length - 1]; // Need to check this again.
      dispatchOrder.origin = {
        name: origins.filter(t => t.originalOrigin === true).map(t => t),
        appointment: origins.filter(t => t.originalOrigin === true).map(t => formatEpochToDateTime(t.appointment)),
        isOpenTime: origins.filter(t => t.originalOrigin === true).map(t => t.openTime),
        openAppointment: origins.filter(t => t.originalOrigin === true).map(t => t.openAppointment),
        puNumber: origins.filter(t => t.originalOrigin === true).map(t => t.puNumber),
        puLocation: origins.filter(t => t.originalOrigin === true).map(t => t.puLocation),
        notes: origins.filter(t => t.originalOrigin === true).map(t => t.notes),
      };

      dispatchOrder.originTabs = origins.filter(t => t.originalOrigin === true).map((t, index) => {
        return (`Origin ${index + 1}`);
      });

      dispatchOrder.destination = {
        name: destinations.filter(t => t.originalDestination).map(t => t),
        appointment: destinations.filter(t => t.originalDestination).map(t => formatEpochToDateTime(t.appointment)),
        isOpenTime: destinations.filter(t => t.originalDestination).map(t => t.openTime),
        openAppointment: destinations.filter(t => t.originalDestination).map(t => t.openAppointment),
        referenceNumber: destinations.filter(t => t.originalDestination).map(t => t.referenceNumber),
        notes: destinations.filter(t => t.originalDestination).map(t => t.notes),
      };
      dispatchOrder.destinationTabs = destinations.filter(t => t.originalDestination).map((c, index) => {
        return (`Destination ${index}+1`);
      });
    }
    if (container) {
      dispatchOrder.container = {
        line: container,
        deliveryContainerNumber: container.deliveryContainerNumber,
        pickupContainerNumber: container.pickupContainerNumber,
        notes: container.notes,
        lastFreeDate: container.lastFreeDate === '0' ? null : container.lastFreeDate,
        bookingNumber: container.bookingNumber,
        isCustomDropLocation: container.isCustomDropLocation,
        dropLocation: container.dropLocation,
      };
    }
    if (chassis) {
      dispatchOrder.chassis = {
        company: chassis,
        chassisNumber: chassis.chassisNumber,
        chassisSplit: chassis.isChassisSplit,
      };
    }
    return dispatchOrder;
  }

  /* OTR Orders */

  static parseOTRDispatchOrder(order) {
    const { id, broker, legs, expenses, truck } = order;
    /* Parse Order to construct OrderForm */
    const dispatchOrder = {};
    dispatchOrder.id = id;
    dispatchOrder.summary = {
      broker: broker,
      brokerOrderNumber: order.brokerOrderNumber,
      loadType: order.loadType,
      lineHaulCharges: order.lineHaulCharges,
      fuelCharges: order.fuelCharges,
      fuelChargesType: order.fuelChargesType,
      appointment: order.appointment,
      notes: order.notes,
      expenses: expenses,
      weight: order.weight,
      length: order.length,
      weightUnit: order.weightUnit,
      lengthUnit: order.lengthUnit,
      palletQty: order.palletQty,
      temperature: order.temperature,
      tempUnit: order.tempUnit,      
    };
    const firstLeg = legs && legs.length > 0 ? legs[legs.length-1] : null;
    if (firstLeg) {
      //Update distance
      dispatchOrder.summary.distance = firstLeg.distance;
      dispatchOrder.summary.distanceUnit = firstLeg.distanceUnit;
      dispatchOrder.summary.duration = firstLeg.duration;

      const { origins, destinations } = firstLeg; // Need to check this again.
      dispatchOrder.origin = origins.map(loc => ({...loc, appointment: formatEpochToDateTime(loc.appointment)}));

      dispatchOrder.originTabs = origins.filter(t => t.originalOrigin === true).map((t, index) => {
        return (`Origin ${index + 1}`);
      });

      dispatchOrder.destination = destinations.map(loc => ({...loc, appointment: formatEpochToDateTime(loc.appointment)}));
      dispatchOrder.destinationTabs = destinations.filter(t => t.originalDestination).map((c, index) => {
        return (`Destination ${index}+1`);
      });
    }
    dispatchOrder.equipment = {
      equipmentId: order.equipmentId,
      equipmentType: order.equipmentType,
      truckId: truck && truck.id,
      truck: truck,
      startMileage: order.startMileage,
      endMileage: order.endMileage,
      trailerNumber: order.trailerNumber,
      trailerLicense: order.trailerLicense,
      secondTrailerNumber: order.secondTrailerNumber,
      secondTrailerLicense: order.secondTrailerLicense,
    };
    return dispatchOrder;
  }

  /**
   * Utility method that checks if passed value is valid email
   *
   * @method testEmail
   * @param value
   * @returns {boolean}
   */

  static checkEmail(email) {
    var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }
}